import type { ReactNode } from "react";
import React, { createContext, useContext } from "react";
import type {
  CategoryScrollHook} from "@/hooks/useCategoryScroll";
import {
  useCategoryScroll
} from "@/hooks/useCategoryScroll";

/**
 * Context for sharing category scroll functionality across components
 */
const CategoryScrollContext = createContext<CategoryScrollHook | undefined>(
  undefined
);

/**
 * Props for the CategoryScrollProvider component
 */
interface CategoryScrollProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps children with category scroll functionality
 */
export const CategoryScrollProvider: React.FC<CategoryScrollProviderProps> = ({
  children,
}) => {
  const categoryScrollHook = useCategoryScroll();

  return (
    <CategoryScrollContext.Provider value={categoryScrollHook}>
      {children}
    </CategoryScrollContext.Provider>
  );
};

/**
 * Custom hook for using the category scroll context
 * @throws Error if used outside of CategoryScrollProvider
 */
export const useCategoryScrollContext = (): CategoryScrollHook => {
  const context = useContext(CategoryScrollContext);

  if (context === undefined) {
    throw new Error(
      "useCategoryScrollContext must be used within a CategoryScrollProvider"
    );
  }

  return context;
};
